import React from 'react';
import type { PlanningSplit } from '@/modules/pipelines/data/metrics';
import {
  BreakdownBar,
  BreakdownLegend,
  type BreakdownSegment,
} from '@/modules/pipelines/components/deep-dive/asset-metrics/breakdown-bar';
import styles from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-metrics.module.scss';

export interface AssetMetricsSummary {
  activeNotices?: PlanningSplit;
}

export interface AssetMetricsProps {
  summary?: AssetMetricsSummary;
}

function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 10_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function Tile({
  label,
  value,
  sub,
  children,
}: React.PropsWithChildren<{ label: string; value?: string; sub?: string }>) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileLabel}>{label}</div>
      <div className={value === undefined ? styles.tileValueEmpty : styles.tileValue}>
        {value ?? 'No data'}
      </div>
      {sub && <div className={styles.tileSub}>{sub}</div>}
      {children}
    </div>
  );
}

function ActiveNoticesTile({ counts }: { counts?: PlanningSplit }) {
  if (!counts) return <Tile label={'Active notices'} />;

  const segments: BreakdownSegment[] = [
    {
      key: 'unplanned',
      label: 'Unplanned',
      count: counts.unplanned,
      color: 'var(--status-critical, #d03b3b)',
    },
    {
      key: 'planned',
      label: 'Planned',
      count: counts.planned,
      color: 'var(--status-planned, #2a78d6)',
    },
    {
      key: 'unknown',
      label: 'Unknown',
      count: counts.unknown,
      color: 'var(--status-neutral-soft, #d6d6d1)',
    },
  ];

  const total = counts.unplanned + counts.planned + counts.unknown;

  return (
    <Tile label={'Active notices'} value={formatCount(total)} sub={'Still active notices'}>
      <BreakdownBar segments={segments} total={total} />
      <BreakdownLegend segments={segments} />
    </Tile>
  );
}

export default function AssetMetrics({ summary }: AssetMetricsProps) {
  return (
    <div className={styles.kpiRow}>
      <ActiveNoticesTile counts={summary?.activeNotices} />
    </div>
  );
}
